<template>
  <modal
    title="Notify customer of schedule change?"
    class-name="small-modal"
    @hide-modal="handleDismiss"
  >
    <p class="mb-2">
      You've updated this appointment's scheduled time and/or technician.
    </p>
    <p>Would you like to notify the customer of this change?</p>
    <template #footer>
      <div class="modal-footer d-flex justify-content-end" style="gap: 0.5rem">
        <SeraButton
          type="secondary"
          data-cy="dont-notify-btn"
          :disabled="saving"
          @click="handleDontNotify"
        >
          Don't Notify
        </SeraButton>
        <SeraButton
          data-cy="notify-btn"
          :disabled="saving"
          @click="handleNotify"
        >
          <span v-if="!saving">Notify Customer</span>
          <Spinner v-else message="Saving..." />
        </SeraButton>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useStore } from "vuex";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Spinner from "@/components/Spinner.vue";

interface PendingPayload {
  appointmentId: string | number;
  arrival_window_start?: string;
  arrival_window_end?: string;
  scheduled_time?: string;
  duration_minutes?: number;
  technician_profile_id?: number | null;
}

const props = withDefaults(
  defineProps<{
    pendingPayload: PendingPayload;
    actionName?: string;
  }>(),
  { actionName: "updateAppointmentEvent" }
);

const emit = defineEmits<{
  "hide-modal": [];
}>();

const store = useStore();
const saving = ref(false);
let resolved = false;

const submitUpdate = async (skipNotifications: boolean) => {
  if (resolved) return;
  resolved = true;
  saving.value = true;
  try {
    await store.dispatch(props.actionName, {
      ...props.pendingPayload,
      skip_notifications: skipNotifications
    });
    window.notify.success("Appointment updated");
  } catch {
    window.notify.error("Failed to update appointment");
  } finally {
    saving.value = false;
    emit("hide-modal");
  }
};

const handleNotify = () => submitUpdate(false);
const handleDontNotify = () => submitUpdate(true);
const handleDismiss = () => submitUpdate(true);

defineExpose({ handleDismiss, saving });
</script>
